import { useEffect, useState } from "react";

const getOneTrustBanner = (): HTMLElement | null => {
    if (typeof document === "undefined") {
        return null;
    }
    return document.getElementById("onetrust-banner-sdk");
};

const computeBannerVisibility = (banner: HTMLElement | null): boolean => {
    if (!banner || typeof window === "undefined") {
        return false;
    }

    const style = window.getComputedStyle(banner);
    return style.display !== "none";
};

/**
 * Tracks whether the OneTrust consent banner is visible on the page.
 */
export const useOneTrustBannerVisible = (): boolean | undefined => {
    const [isVisible, setIsVisible] = useState<boolean>(false);

    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") {
            return;
        }

        const updateVisibility = () => {
            setIsVisible(computeBannerVisibility(getOneTrustBanner()));
        };

        updateVisibility();

        const banner = getOneTrustBanner();
        if (!banner) {
            return;
        }

        const observer = new MutationObserver(() => {
            updateVisibility();
        });

        observer.observe(banner, {
            attributes: true,
            attributeFilter: ["style", "class"],
            childList: false,
            subtree: false,
        });

        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["class"],
            childList: false,
            subtree: false,
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    return isVisible;
};
